(function (global) {
  'use strict';

  var COMPONENT_NAME = 'vl-blog-double-add-to-cart';
  var DATA_SELECTOR = '[data-vl-blog-double-data]';
  var OPTION_SELECTOR = '[data-vl-blog-double-option]';
  var SELECTION_SELECTOR = '[data-vl-blog-double-selection]';
  var PRIMARY_INPUT_SELECTOR = '[data-vpc-variant-input]';
  var SUBMIT_SELECTOR = '[data-vl-blog-double-submit]';

  function toIdSet(ids) {
    return new Set((ids || []).map(function (id) { return String(id); }));
  }

  function getVisibleVariants(variants, hiddenVariantIds) {
    var hiddenIds = hiddenVariantIds instanceof Set ? hiddenVariantIds : toIdSet(hiddenVariantIds);
    return (variants || []).filter(function (variant) {
      return !hiddenIds.has(String(variant.id));
    });
  }

  function normalizeInitialVariant(variants, requestedId) {
    if (!Array.isArray(variants) || variants.length === 0) return null;
    var requested = String(requestedId || '');
    return variants.find(function (variant) {
      return String(variant.id) === requested && variant.available;
    }) || variants.find(function (variant) {
      return variant.available;
    }) || variants.find(function (variant) {
      return String(variant.id) === requested;
    }) || variants[0];
  }

  function getVariantOption(variant, index) {
    if (!variant) return null;
    if (Array.isArray(variant.options)) return variant.options[index];
    return variant['option' + (index + 1)];
  }

  function selectedOptionsFromVariant(optionNames, variant) {
    return (optionNames || []).reduce(function (result, optionName, index) {
      result[optionName] = getVariantOption(variant, index);
      return result;
    }, {});
  }

  function findExactVariant(variants, optionNames, selectedOptions) {
    return (variants || []).find(function (variant) {
      return (optionNames || []).every(function (optionName, index) {
        return getVariantOption(variant, index) === selectedOptions[optionName];
      });
    }) || null;
  }

  function findFallbackVariant(variants, optionNames, selectedOptions, changedOptionName) {
    if (!Array.isArray(variants) || variants.length === 0) return null;
    var changedIndex = optionNames.indexOf(changedOptionName);
    var changedValue = selectedOptions[changedOptionName];
    var candidates = variants.filter(function (variant) {
      return changedIndex < 0 || getVariantOption(variant, changedIndex) === changedValue;
    });
    if (candidates.length === 0) candidates = variants.slice();

    return candidates.slice().sort(function (left, right) {
      if (Boolean(left.available) !== Boolean(right.available)) return left.available ? -1 : 1;
      var leftScore = optionNames.reduce(function (score, optionName, index) {
        return score + (getVariantOption(left, index) === selectedOptions[optionName] ? 1 : 0);
      }, 0);
      var rightScore = optionNames.reduce(function (score, optionName, index) {
        return score + (getVariantOption(right, index) === selectedOptions[optionName] ? 1 : 0);
      }, 0);
      return rightScore - leftScore;
    })[0] || null;
  }

  function applyProductRules(selectedOptions, optionName, value, config) {
    var next = Object.assign({}, selectedOptions);
    next[optionName] = value;
    if (config && config.highStepForcesLarge && optionName === 'Style' && value === 'High-Step') {
      next.Size = 'Large';
    }
    return next;
  }

  function isOptionDisabledByRule(optionName, optionValue, selectedOptions, config) {
    return Boolean(
      config
      && config.disableRegularForHighStep
      && optionName === 'Size'
      && optionValue === 'Regular'
      && selectedOptions.Style === 'High-Step'
    );
  }

  function getOptionState(variants, optionNames, selectedOptions, optionName, value, config) {
    var optionIndex = optionNames.indexOf(optionName);
    var matches = optionIndex < 0 ? [] : variants.filter(function (variant) {
      return getVariantOption(variant, optionIndex) === value;
    });
    var disabledByRule = isOptionDisabledByRule(optionName, value, selectedOptions, config);
    return {
      selected: selectedOptions[optionName] === value,
      exists: matches.length > 0,
      available: !disabledByRule && matches.some(function (variant) { return variant.available; }),
      disabledByRule: disabledByRule
    };
  }

  function normalizeFreeItemIds(value) {
    return String(value || '')
      .split(',')
      .map(function (id) { return id.trim(); })
      .filter(Boolean);
  }

  function buildCombinedFreeItemValue(baseValue, secondVariantId) {
    var ids = normalizeFreeItemIds(baseValue);
    if (secondVariantId) ids.push(String(secondVariantId));
    return ids.length > 0 ? ids.join(',') + ',' : '';
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var value = ((Number(cents) || 0) / 100).toFixed(precision);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  }

  function formatMoney(cents, format) {
    if (global.Shopify && typeof global.Shopify.formatMoney === 'function') {
      return global.Shopify.formatMoney(cents, format).replace(/([.,])00(?=\D*$)/, '');
    }
    var moneyFormat = format || '${{amount}}';
    var match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return '$' + formatWithDelimiters(cents, 2, ',', '.');
    var token = match[1];
    var formatted = {
      amount: formatWithDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ',')
    }[token] || formatWithDelimiters(cents, 2, ',', '.');
    return moneyFormat.replace(match[0], formatted).replace(/([.,])00(?=\D*$)/, '');
  }

  function normalizeDiscountType(type) {
    var normalized = String(type || '').toLowerCase().replace(/[-\s]+/g, '_');
    if (normalized === 'fixed' || normalized === 'fixed_amount') return 'fixed_amount';
    if (normalized === 'percent' || normalized === 'percentage') return 'percentage';
    return 'none';
  }

  function calculateDiscount(subtotalCents, type, value) {
    var subtotal = Math.max(0, Math.round(Number(subtotalCents) || 0));
    var discountType = normalizeDiscountType(type);
    var discountValue = Math.max(0, Number(value) || 0);
    var savings = 0;

    if (discountType === 'fixed_amount') {
      savings = Math.round(discountValue * 100);
    } else if (discountType === 'percentage') {
      discountValue = Math.min(100, discountValue);
      savings = Math.round(subtotal * discountValue / 100);
    }

    savings = Math.min(subtotal, Math.max(0, savings));
    var percentage = subtotal > 0 ? savings / subtotal * 100 : 0;
    return {
      active: savings > 0,
      type: discountType,
      value: discountValue,
      subtotal: subtotal,
      savings: savings,
      total: subtotal - savings,
      percentage: percentage
    };
  }

  function formatDiscountPercentage(value) {
    var rounded = Math.round((Number(value) || 0) * 10) / 10;
    return String(rounded).replace(/\.0$/, '') + '%';
  }

  function getDiscountConfig(element, data) {
    var slot = element.closest && element.closest('[data-vl-blog-product-slot]');
    var type = slot && slot.dataset.vlBlogDiscountType;
    var value = slot && slot.dataset.vlBlogDiscountValue;
    return {
      type: normalizeDiscountType(type || (data && data.discountType)),
      value: Number(value != null ? value : data && data.discountValue) || 0
    };
  }

  function variantUrl(productUrl, variantId) {
    try {
      var parsedUrl = new URL(productUrl, 'https://velotric.invalid');
      parsedUrl.searchParams.set('variant', String(variantId));
      if (/^https?:\/\//.test(productUrl)) return parsedUrl.href;
      return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    } catch (error) {
      return productUrl + (productUrl.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variantId;
    }
  }

  function variantImageUrl(variant, fallbackImageUrl) {
    var imageUrl = variant && variant.featured_image && variant.featured_image.src;
    if (!imageUrl) return fallbackImageUrl || '';
    if (/[?&]width=/.test(imageUrl)) return imageUrl;
    return imageUrl + (imageUrl.indexOf('?') === -1 ? '?' : '&') + 'width=800&height=600&format=webp';
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = hidden;
    element.classList.toggle('hidden', hidden);
  }

  function parseComponentData(element) {
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      console.warn('[VL Blog double add] Invalid component data.', error);
      return null;
    }
  }

  var HTMLElementBase = global.HTMLElement || class {};

  class VlBlogDoubleAddToCart extends HTMLElementBase {
    connectedCallback() {
      if (this.controllerAbort) return;

      this.productCard = this.closest('product-card');
      this.data = parseComponentData(this.querySelector(DATA_SELECTOR));
      this.primaryInput = this.querySelector(PRIMARY_INPUT_SELECTOR);
      this.formWrap = this.querySelector('.product-card-2-form');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector(SUBMIT_SELECTOR);
      if (!this.productCard || !this.data || !this.primaryInput || !this.formWrap || !this.submitButton) return;

      this.variants = getVisibleVariants(this.data.variants, this.data.hiddenVariantIds);
      this.discountConfig = getDiscountConfig(this, this.data);
      var initialVariant = normalizeInitialVariant(this.variants, this.data.initialVariantId);
      if (!initialVariant) {
        this.submitButton.disabled = true;
        return;
      }

      this.selectionVariants = { 1: initialVariant, 2: initialVariant };
      this.selectedOptionsBySelection = {
        1: selectedOptionsFromVariant(this.data.optionNames, initialVariant),
        2: selectedOptionsFromVariant(this.data.optionNames, initialVariant)
      };
      this.baseFreeItems = this.formWrap.dataset.vlBlogFreeItems || '';
      this.activeSelectionIndex = 1;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
      this.controllerAbort = new AbortController();
      this.addEventListener('click', this.handleClick.bind(this), { signal: this.controllerAbort.signal });
      this.addEventListener('focusin', this.handleFocusIn.bind(this), { signal: this.controllerAbort.signal });
      this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true, signal: this.controllerAbort.signal });
      this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true, signal: this.controllerAbort.signal });
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this), {
          capture: true,
          signal: this.controllerAbort.signal
        });
      }

      this.renderSelection(1);
      this.renderSelection(2);
      this.setActiveSelection(1);
      this.renderPrimaryCard();
      this.syncCombinedState();
    }

    disconnectedCallback() {
      if (this.controllerAbort) this.controllerAbort.abort();
      this.controllerAbort = null;
    }

    handleClick(event) {
      var selection = event.target.closest(SELECTION_SELECTOR);
      if (!selection || !this.contains(selection)) return;
      var selectionIndex = selection ? Number(selection.dataset.vlBlogDoubleSelection) : 0;
      if (!this.selectionVariants[selectionIndex]) return;
      this.setActiveSelection(selectionIndex);

      var option = event.target.closest(OPTION_SELECTOR);
      if (!option || !selection.contains(option) || option.disabled) return;
      var optionName = option.dataset.optionName;
      var optionValue = option.dataset.optionValue;
      var nextOptions = applyProductRules(
        this.selectedOptionsBySelection[selectionIndex],
        optionName,
        optionValue,
        this.data
      );
      var nextVariant = findExactVariant(this.variants, this.data.optionNames, nextOptions);
      if (!nextVariant || !nextVariant.available) {
        nextVariant = findFallbackVariant(
          this.variants,
          this.data.optionNames,
          nextOptions,
          optionName
        );
      }
      if (!nextVariant) return;
      this.selectionVariants[selectionIndex] = nextVariant;
      this.selectedOptionsBySelection[selectionIndex] = selectedOptionsFromVariant(this.data.optionNames, nextVariant);
      this.renderSelection(selectionIndex);
      if (selectionIndex === 1) this.renderPrimaryCard();
      this.syncCombinedState();
    }

    handleFocusIn(event) {
      var selection = event.target.closest(SELECTION_SELECTOR);
      if (!selection || !this.contains(selection)) return;
      this.setActiveSelection(Number(selection.dataset.vlBlogDoubleSelection));
    }

    handleTouchStart(event) {
      if (event.touches && event.touches.length === 1) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
        this.touchStartTime = Date.now();
      }
    }

    handleTouchEnd(event) {
      if (!event.changedTouches || event.changedTouches.length !== 1) return;
      var touch = event.changedTouches[0];
      var deltaX = touch.clientX - this.touchStartX;
      var deltaY = touch.clientY - this.touchStartY;
      var threshold = Math.max(40, Math.min(90, this.clientWidth * 0.16));
      if (Date.now() - this.touchStartTime > 700 || Math.abs(deltaX) < threshold || Math.abs(deltaX) < Math.abs(deltaY) * 1.15) return;
      this.setActiveSelection(this.activeSelectionIndex + (deltaX < 0 ? 1 : -1));
    }

    setActiveSelection(selectionIndex) {
      if (!this.selectionVariants[selectionIndex]) return;
      this.activeSelectionIndex = selectionIndex;
      this.dataset.vlBlogActiveSelection = String(selectionIndex);
      this.querySelectorAll(SELECTION_SELECTOR).forEach(function (selection) {
        var isActive = Number(selection.dataset.vlBlogDoubleSelection) === selectionIndex;
        selection.classList.toggle('is-active', isActive);
        var activateButton = selection.querySelector('[data-vl-blog-double-activate]');
        if (activateButton) {
          activateButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          var editLabel = activateButton.querySelector('.vl-blog-double-add__selection-edit');
          if (editLabel) editLabel.textContent = isActive ? 'Selected' : 'Select';
        }
      });
    }

    handleSubmit(event) {
      this.syncCombinedState();
      if (!this.isCombinedAvailable()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    getPrimaryVariant() {
      return this.selectionVariants[1] || null;
    }

    isCombinedAvailable() {
      var primaryVariant = this.getPrimaryVariant();
      var secondaryVariant = this.selectionVariants[2];
      return Boolean(primaryVariant && primaryVariant.available && secondaryVariant && secondaryVariant.available);
    }

    renderSelection(selectionIndex) {
      var self = this;
      var selection = this.querySelector('[data-vl-blog-double-selection="' + selectionIndex + '"]');
      if (!selection) return;
      var selectedOptions = this.selectedOptionsBySelection[selectionIndex];
      var selectedVariant = this.selectionVariants[selectionIndex];

      selection.querySelectorAll(OPTION_SELECTOR).forEach(function (option) {
        var optionName = option.dataset.optionName;
        var optionValue = option.dataset.optionValue;
        var state = getOptionState(
          self.variants,
          self.data.optionNames,
          selectedOptions,
          optionName,
          optionValue,
          self.data
        );
        option.hidden = !state.exists;
        option.disabled = !state.available;
        option.classList.toggle('selected', state.selected);
        option.classList.toggle('available', state.available);
        option.classList.toggle('disabled', !state.available);
        option.classList.toggle('sold-out', state.exists && !state.available && !state.disabledByRule);
        option.setAttribute('aria-checked', state.selected ? 'true' : 'false');
        option.setAttribute('aria-disabled', state.available ? 'false' : 'true');
      });

      selection.querySelectorAll('[data-vl-blog-double-current-option]').forEach(function (element) {
        element.textContent = selectedOptions[element.dataset.optionName] || '';
      });
      selection.querySelectorAll('[data-vl-blog-double-rider-height]').forEach(function (element) {
        element.classList.toggle('selected', element.dataset.variantId === String(selectedVariant.id));
      });
      selection.querySelectorAll('[data-vl-blog-double-selection-image]').forEach(function (image) {
        var imageUrl = variantImageUrl(selectedVariant, self.data.fallbackImageUrl);
        if (!imageUrl) return;
        image.src = imageUrl;
        image.setAttribute('data-src', imageUrl);
      });
      this.dataset['vlBlogBike' + selectionIndex + 'VariantId'] = String(selectedVariant.id);
    }

    renderPrimaryCard() {
      var variant = this.getPrimaryVariant();
      if (!variant) return;
      var selectedOptions = this.selectedOptionsBySelection[1];
      var imageUrl = variantImageUrl(variant, this.data.fallbackImageUrl);
      var compareAtPrice = Number(variant.compare_at_price) || 0;
      var price = Number(variant.price) || 0;

      this.productCard.dataset.variant = String(variant.id);
      if (this.data.optionNames.length > 1) {
        this.productCard.dataset.styleValue = selectedOptions[this.data.optionNames[0]] || '';
      }
      if (this.data.optionNames.length > 2) {
        this.productCard.dataset.sizeValue = selectedOptions[this.data.optionNames[1]] || '';
      }

      this.productCard.querySelectorAll('[data-vpc-link]').forEach(function (link) {
        link.href = variantUrl(this.data.productUrl, variant.id);
      }, this);
      this.productCard.querySelectorAll('[data-vpc-image]').forEach(function (image) {
        if (!imageUrl) return;
        image.src = imageUrl;
        image.setAttribute('data-src', imageUrl);
      });
      this.productCard.querySelectorAll('[data-vpc-price]').forEach(function (element) {
        element.innerHTML = formatMoney(price, this.data.moneyFormat);
      }, this);
      this.productCard.querySelectorAll('[data-vpc-compare-price]').forEach(function (element) {
        element.innerHTML = formatMoney(compareAtPrice, this.data.moneyFormat);
        setHidden(element, compareAtPrice <= price);
      }, this);
      this.primaryInput.value = String(variant.id);
      this.primaryInput.setAttribute('value', String(variant.id));
    }

    syncCombinedState() {
      var primaryVariant = this.getPrimaryVariant();
      var secondaryVariant = this.selectionVariants[2];
      var primaryPrice = primaryVariant ? Number(primaryVariant.price) || 0 : 0;
      var secondaryPrice = secondaryVariant ? Number(secondaryVariant.price) || 0 : 0;
      var priceElements = this.querySelectorAll('[data-vl-blog-double-selection-price]');
      if (priceElements[0]) priceElements[0].innerHTML = formatMoney(primaryPrice, this.data.moneyFormat);
      if (priceElements[1]) priceElements[1].innerHTML = formatMoney(secondaryPrice, this.data.moneyFormat);
      var totalNote = this.querySelector('[data-vl-blog-double-total-note]');
      if (totalNote) {
        var optionNames = this.data.optionNames || [];
        var optionLabel = function (variant) {
          return optionNames.map(function (name, index) {
            return getVariantOption(variant, index);
          }).filter(Boolean).join('/');
        };
        totalNote.textContent = [optionLabel(primaryVariant), optionLabel(secondaryVariant)].filter(Boolean).join(' + ');
      }

      this.formWrap.dataset.freeitem = buildCombinedFreeItemValue(
        this.baseFreeItems,
        secondaryVariant && secondaryVariant.id
      );

      var isAvailable = this.isCombinedAvailable();
      this.submitButton.disabled = !isAvailable;
      var label = this.querySelector('[data-vl-blog-double-submit-label]');
      if (label) {
        label.innerHTML = isAvailable
          ? 'Add both to cart'
          : 'Sold out';
      }
      var discount = calculateDiscount(
        primaryPrice + secondaryPrice,
        this.discountConfig.type,
        this.discountConfig.value
      );
      var total = this.querySelector('[data-vl-blog-double-total]');
      if (total) total.innerHTML = formatMoney(discount.total, this.data.moneyFormat);
      var originalTotal = this.querySelector('[data-vl-blog-double-original-total]');
      if (originalTotal) {
        originalTotal.innerHTML = formatMoney(discount.subtotal, this.data.moneyFormat);
        setHidden(originalTotal, !discount.active);
      }
      var discountSummary = this.querySelector('[data-vl-blog-double-discount]');
      setHidden(discountSummary, !discount.active);
      var discountPercent = this.querySelector('[data-vl-blog-double-discount-percent]');
      if (discountPercent) {
        discountPercent.hidden = false;
        discountPercent.textContent = discount.type === 'percentage'
          ? 'SAVE ' + formatDiscountPercentage(discount.percentage)
          : formatMoney(discount.savings, this.data.moneyFormat) + ' OFF';
      }
      var discountAmount = this.querySelector('[data-vl-blog-double-discount-amount]');
      if (discountAmount) {
        discountAmount.textContent = '';
      }
      this.dataset.vlBlogDiscountType = discount.type;
      this.dataset.vlBlogDiscountActive = discount.active ? 'true' : 'false';

      var status = this.querySelector('[data-vl-blog-double-status]');
      if (status) {
        status.textContent = isAvailable
          ? 'Bike 1 and Bike 2 are ready to add to cart.'
          : 'One or both selected bikes are unavailable.';
      }
    }
  }

  global.VlBlogDoubleAddToCartTest = {
    toIdSet: toIdSet,
    getVisibleVariants: getVisibleVariants,
    normalizeInitialVariant: normalizeInitialVariant,
    selectedOptionsFromVariant: selectedOptionsFromVariant,
    findExactVariant: findExactVariant,
    findFallbackVariant: findFallbackVariant,
    applyProductRules: applyProductRules,
    getOptionState: getOptionState,
    normalizeFreeItemIds: normalizeFreeItemIds,
    buildCombinedFreeItemValue: buildCombinedFreeItemValue,
    formatMoney: formatMoney,
    normalizeDiscountType: normalizeDiscountType,
    calculateDiscount: calculateDiscount,
    formatDiscountPercentage: formatDiscountPercentage,
    variantUrl: variantUrl,
    variantImageUrl: variantImageUrl
  };

  if (global.customElements && !global.customElements.get(COMPONENT_NAME)) {
    global.customElements.define(COMPONENT_NAME, VlBlogDoubleAddToCart);
  }
})(typeof window !== 'undefined' ? window : globalThis);
